import { put, select, takeLatest, delay } from 'redux-saga/effects';
import {
  FETCH_MAIL_SEARCH_PROJECT_COMMIT,
  FETCH_MAIL_SEARCH_PROJECT_ROLLBACK,
} from '../actions/projects/search';
import {
  FETCH_MAIL_SEARCH_ME_COMMIT,
  FETCH_MAIL_SEARCH_ME_ROLLBACK,
} from '../actions/me/searchMe';
import {
  SET_CURRENT_MAIL_SEARCH_ME,
  SET_CURRENT_MAIL_SEARCH_PROJECT,
} from '../actions/actionControllers/searchActionController';
import { fetchMessagesSagaAction } from '../actions/sagas';
import {
  getCurrentSearchTerm,
  getMailSearchIds,
  getMailSearchIsFetching,
} from '../../../apps/main/rootReducer';
import { MailFolderId, ProjectId } from '../../../models/Types';
import { Message } from '../../../models/Message';
import { LOGGED_OUT } from '../../auth/actions';
import { SAGA_REBUILD } from '../../../util/sagas';

const MIN_VISIBLE_ITEMS = 20;
const MAX_AUTO_PAGES_PER_SEARCH = 10;
const AUTO_PAGINATE_DELAY_MS = 100;

const autoPaginateCount = new Map<ProjectId, number>();

interface SearchCommitAction {
  type: string;
  payload: {
    messages?: Message[];
    nextLink?: string;
  };
  meta: {
    projectId?: ProjectId;
    nextLink?: string;
    mailFolderId?: MailFolderId;
  };
}

const hasNextPage = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0;

function* handleSearchCommit(action: SearchCommitAction) {
  try {
    const { payload, type, meta } = action;
    if (!hasNextPage(payload?.nextLink)) return;

    const projectId: ProjectId =
      type === FETCH_MAIL_SEARCH_ME_COMMIT ? 'me' : meta?.projectId;
    if (!projectId) return;

    const itemIds: string[] = yield select(
      (state) => getMailSearchIds(state, projectId) ?? []
    );
    if (itemIds.length >= MIN_VISIBLE_ITEMS) {
      autoPaginateCount.delete(projectId);
      return;
    }

    const count = (autoPaginateCount.get(projectId) ?? 0) + 1;
    if (count > MAX_AUTO_PAGES_PER_SEARCH) {
      autoPaginateCount.delete(projectId);
      return;
    }

    yield delay(AUTO_PAGINATE_DELAY_MS);

    const isFetching: boolean = yield select((state) =>
      getMailSearchIsFetching(state, projectId)
    );
    if (isFetching) return;

    const currentSearchTerm = yield select((state) =>
      getCurrentSearchTerm(state, projectId)
    );
    if (!currentSearchTerm) return;

    autoPaginateCount.set(projectId, count);
    // mailFolderId is ignored by handleMessages when a searchTerm is active
    yield put(fetchMessagesSagaAction(projectId, 'inbox'));
  } catch (error) {
    console.error('Error in watchAutoPaginateSearch', error);
  }
}

function handleSearchReset(action: { projectId?: ProjectId }) {
  if (action.projectId) {
    autoPaginateCount.delete(action.projectId);
  } else {
    autoPaginateCount.clear();
  }
}

function handleSearchRollback(action: {
  type: string;
  meta?: { projectId?: ProjectId };
}) {
  const projectId: ProjectId =
    action.type === FETCH_MAIL_SEARCH_ME_ROLLBACK
      ? 'me'
      : action.meta?.projectId;
  if (projectId) autoPaginateCount.delete(projectId);
}

function handleLifecycleReset() {
  autoPaginateCount.clear();
}

export default function* watchAutoPaginateSearch() {
  yield takeLatest(
    [FETCH_MAIL_SEARCH_PROJECT_COMMIT, FETCH_MAIL_SEARCH_ME_COMMIT],
    handleSearchCommit
  );
  yield takeLatest(
    [SET_CURRENT_MAIL_SEARCH_PROJECT, SET_CURRENT_MAIL_SEARCH_ME],
    handleSearchReset
  );
  yield takeLatest(
    [FETCH_MAIL_SEARCH_PROJECT_ROLLBACK, FETCH_MAIL_SEARCH_ME_ROLLBACK],
    handleSearchRollback
  );
  yield takeLatest([LOGGED_OUT, SAGA_REBUILD], handleLifecycleReset);
}
